import type { Ref } from "vue";
import type { Requests } from "../common/request.common";
import type { ResellerRegistrationResponseTypes } from "../common/response.common";
import { asRemoveHook, asUpdateHook, get, post, postEmpty } from "./common";

export const resellerRegistration = {
    record(data: typeof Requests.ResellerRegistration.Record.Input) {
        return post<typeof data, ResellerRegistrationResponseTypes.Record>(
            `/reseller-registration/record`,
            data,
        );
    },
    create(data: typeof Requests.ResellerRegistration.Create.Input) {
        return post<typeof data>(`/reseller-registration/create`, data);
    },
    obtain(id: string) {
        return get<ResellerRegistrationResponseTypes.Obtain>(`/reseller-registration/obtain/${id}`);
    },
    registrationSetup(
        id: string,
        data: typeof Requests.ResellerRegistration.RegistrationSetup.Input,
    ) {
        return post<typeof data>(`/reseller-registration/registration-setup/${id}`, data);
    },
    advance(id: string) {
        return postEmpty(`/reseller-registration/advance/${id}`);
    },
    surveyCreate(id: string, data: typeof Requests.ResellerRegistration.SurveyCreate.Input) {
        return post<typeof data>(`/reseller-registration/survey-create/${id}`, data);
    },
    surveyDispatch(id: string) {
        return postEmpty(`/reseller-registration/survey-dispatch/${id}`);
    },
    surveyReport(id: string, data: typeof Requests.ResellerRegistration.SurveyReport.Input) {
        return post<typeof data>(`/reseller-registration/survey-report/${id}`, data);
    },
    resellerCreate(id: string, data: typeof Requests.ResellerRegistration.ResellerCreate.Input) {
        return post<typeof data>(`/reseller-registration/reseller-create/${id}`, data);
    },
    productSelect(id: string, data: typeof Requests.ResellerRegistration.ProductSelect.Input) {
        return post<typeof data>(`/reseller-registration/product-select/${id}`, data);
    },
    installationCreate(
        id: string,
        data: typeof Requests.ResellerRegistration.InstallationCreate.Input,
    ) {
        return post<typeof data>(`/reseller-registration/installation-create/${id}`, data);
    },
    installationDispatch(id: string) {
        return postEmpty(`/reseller-registration/installation-dispatch/${id}`);
    },
    provisionCreate(id: string, data: typeof Requests.ResellerRegistration.ProvisionCreate.Input) {
        return post<typeof data>(`/reseller-registration/provision-create/${id}`, data);
    },
    provisionCancel(id: string, data: typeof Requests.ResellerRegistration.ProvisionCancel.Input) {
        return post<typeof data>(`/reseller-registration/provision-cancel/${id}`, data);
    },
    installationFinish(id: string) {
        return postEmpty(`/reseller-registration/installation-finish/${id}`);
    },
    installationReport(
        id: string,
        data: typeof Requests.ResellerRegistration.InstallationReport.Input,
    ) {
        return post<typeof data>(`/reseller-registration/installation-report/${id}`, data);
    },
    provisionFinalize(
        id: string,
        data: typeof Requests.ResellerRegistration.ProvisionFinalize.Input,
    ) {
        return post<typeof data>(`/reseller-registration/provision-finalize/${id}`, data);
    },
    finalize(id: string, data: typeof Requests.ResellerRegistration.Finalize.Input) {
        return post<typeof data>(`/reseller-registration/finalize/${id}`, data);
    },
    cancel(id: string) {
        return postEmpty(`/reseller-registration/cancel/${id}`);
    },
    formHooks: {
        update(id: string, isLoading?: Ref<boolean>) {
            return async (data: typeof Requests.ResellerRegistration.Update.Input) => {
                return await asUpdateHook(
                    post<typeof data>(`/reseller-registration/update/${id}`, data),
                    isLoading,
                );
            };
        },
        remove(id: string, isLoading?: Ref<boolean>) {
            return async (data: typeof Requests.ResellerRegistration.Remove.Input) => {
                return await asRemoveHook(
                    post<typeof data>(`/reseller-registration/remove/${id}`, data),
                    isLoading,
                );
            };
        },
    },
};
